import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { NetworkError } from '@/lib/api';
import { useApi } from '@/lib/api-context';

import {
  clockResponseSchema,
  pauseResponseSchema,
  trackResponseSchema,
  type ClockView,
  type SessionState,
} from './types';

// Lectura del cronómetro de la etapa y sus acciones Pausar y Reanudar (so=420, spec
// §4.1 y §5). La pausa gana (D4): quien llama espera el latido en vuelo antes de pausar.
// Sin red, la pausa queda pendiente en la interfaz y se reintenta al reconectar.

const BASE = '/api/v1/quality/time-engine';

export type ActionResult = 'ok' | 'offline' | 'error';

interface Options {
  /** Empresa activa: entra al queryKey para no servir un reloj de otra empresa. */
  companyId: string | null;
  enabled: boolean;
}

export interface StageClock {
  query: UseQueryResult<ClockView>;
  /** Sesión efectiva: la del servidor, salvo una acción recién hecha o pendiente. */
  session: SessionState | null;
  pausePending: boolean;
  busy: boolean;
  refresh: () => Promise<void>;
  pause: (beforePause: () => Promise<void>) => Promise<ActionResult>;
  resume: () => Promise<ActionResult>;
}

// Solo una petición que no llegó al servidor queda pendiente de reenviar al reconectar.
const outcomeOf = (e: unknown): ActionResult => (e instanceof NetworkError ? 'offline' : 'error');

/**
 * Intención de la persona (so=420 M2/B8): `paused` se mantiene hasta que toca Reanudar,
 * aunque el servidor ya no tenga un tramo abierto (p. ej. lo cerró por inactividad
 * mientras no había red); `active` se suelta en cuanto el servidor confirma que la
 * sesión dejó de estar en pausa, para mostrar el estado real.
 */
type Intent = 'paused' | 'active' | null;

export function useStageClock(flowInstanceId: string, { companyId, enabled }: Options): StageClock {
  const api = useApi();
  const qc = useQueryClient();
  const [intent, setIntent] = useState<Intent>(null);
  const [pausePending, setPausePending] = useState(false);
  const [busy, setBusy] = useState(false);
  const pausedAtRef = useRef<string | null>(null);
  const queryKey = ['stage-clock', companyId, flowInstanceId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const raw = await api.get<unknown>(`${BASE}/clock?flowInstanceId=${flowInstanceId}`);
      return clockResponseSchema.parse(raw).data;
    },
    enabled,
  });

  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: ['stage-clock', companyId, flowInstanceId] }),
    [qc, companyId, flowInstanceId],
  );

  /** Envía la pausa con el instante en que la persona la pidió (so=420 M2). */
  const sendPause = useCallback(async (): Promise<ActionResult> => {
    try {
      const at = pausedAtRef.current ?? new Date().toISOString();
      pauseResponseSchema.parse(await api.post(`${BASE}/pause`, { flowInstanceId, at }));
      return 'ok';
    } catch (e) {
      return outcomeOf(e);
    }
  }, [api, flowInstanceId]);

  const pause = async (beforePause: () => Promise<void>): Promise<ActionResult> => {
    pausedAtRef.current = new Date().toISOString();
    setBusy(true);
    setIntent('paused');
    try {
      await beforePause();
      const result = await sendPause();
      if (result === 'offline') {
        setPausePending(true);
        return result;
      }
      if (result === 'error') {
        setIntent(null);
      }
      await refresh();
      return result;
    } finally {
      setBusy(false);
    }
  };

  const resume = async (): Promise<ActionResult> => {
    setBusy(true);
    setPausePending(false);
    pausedAtRef.current = null;
    setIntent('active');
    try {
      trackResponseSchema.parse(await api.post(`${BASE}/resume`, { flowInstanceId }));
      await refresh();
      return 'ok';
    } catch (e) {
      const result = outcomeOf(e);
      // Sin red la sesión sigue activa en la interfaz: los latidos se guardan como marcas.
      if (result === 'error') {
        setIntent(null);
        await refresh();
      }
      return result;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!pausePending) {
      return undefined;
    }
    const onOnline = (): void => {
      void sendPause().then((result) => {
        if (result === 'offline') {
          return;
        }
        setPausePending(false);
        void refresh();
      });
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [pausePending, sendPause, refresh]);

  const server = query.data?.open === true ? query.data.mine.session : null;

  // La reanudación deja de ser intención cuando el servidor confirma otro estado.
  useEffect(() => {
    if (intent === 'active' && server !== null && server !== 'paused') {
      setIntent(null);
    }
  }, [intent, server]);

  const session = intent ?? server;
  return { query, session, pausePending, busy, refresh, pause, resume };
}
